import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Filter, Search, ArrowUpDown } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import CartSidebar from '../components/CartSidebar';
import PromoCodeInput from '../components/PromoCodeInput';
import { fetchProducts } from '../data/products';
import { useCartStore } from '../store/cartStore';
import { useGeoStore } from '../store/geoStore';
import { useDeliveryRegionStore } from '../store/deliveryRegionStore';
import { isLocalOnlyCategory } from '../lib/brandMap';
import { useNaturesStore } from '../store/naturesStore';
import RegionSwitcher from '../components/RegionSwitcher';
import { trackPageView } from '../lib/analytics';
import { formatPrice } from '../lib/utils';
import type { Product } from '../types';
import { SITE_NAME } from '../lib/brand';
import { brandLabel } from '../lib/brandMap';
// @module:store-package-imports — 套餐相關 import 由 package-combo 注入於此行之下
import { Package, X, Plus } from 'lucide-react';
import { apiFetchJson } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import type { PackageConfigPublic } from '../types';

const BRAND_GROUP_OPTIONS = [
  { slug: 'hk_brand', name: '香港品牌' },
  { slug: 'overseas_brand', name: '海外品牌' },
];

// Restaurant offers moved to the dedicated /offers page — never show them in the store.
const STORE_EXCLUDED_BRAND_GROUPS = ['local_restaurant', 'mainland_restaurant'];

const CATEGORY_LABELS: Record<string, string> = {
  'kung-heated': '滾水6腳 · 加熱食品',
  'kung-frozen': '滾水6腳 · 急凍食品',
  'kung-wonton': '滾水6腳 · 雲吞',
  'kung-sauce': '滾水6腳 · 醬汁及配菜',
  'live-sauce': '直播嚴選 · 醬料',
  'live-seafood': '直播嚴選 · 海味',
  'live-set': '直播嚴選 · 套裝',
  overseas: '海外直郵 · 常溫耐儲',
  'overseas-gift': '海外直郵 · 禮盒',
  alcohol: '酒品類',
};

// Seeded PRNG so the random order stays stable during the current page session.
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const DAY_SECONDS = 86400;

function getProductWeight(createdAt?: number): number {
  if (!createdAt) return 1;
  const ageDays = (Math.floor(Date.now() / 1000) - createdAt) / DAY_SECONDS;
  if (ageDays <= 7) return 5;
  if (ageDays <= 30) return 2;
  return 1;
}

function weightedShuffle<T>(items: T[], weightFn: (item: T) => number, seed: number): T[] {
  const rng = mulberry32(seed);
  const scored = items.map((item) => ({
    item,
    score: -Math.log(Math.max(rng(), 1e-10)) / weightFn(item),
  }));
  scored.sort((a, b) => a.score - b.score);
  return scored.map((s) => s.item);
}

export default function Store() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [brandGroupFilter, setBrandGroupFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<'default' | 'new' | 'price-asc' | 'price-desc'>('default');
  const [cartOpen, setCartOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [shuffleSeed, setShuffleSeed] = useState(() => Date.now());
  const ITEMS_PER_PAGE = 20;

  const totalItems = useCartStore((s) => s.totalItems());
  const subtotal = useCartStore((s) => s.subtotal());
  // 地理偵測＋地址為王（客人 2026-09-05）：
  //   visitorRegion（IP）只做推送排序，不限制瀏覽；
  //   deliveryRegion（收貨地址）='OS' 時隱藏僅本地可售商品。
  const visitorRegion = useGeoStore((s) => s.visitorRegion);
  useNaturesStore((s) => s.loaded); // shipping-zones v2 定性映射載入後排序/過濾重算
  const deliveryRegion = useDeliveryRegionStore((s) => s.deliveryRegion);
  const [geoBannerDismissed, setGeoBannerDismissed] = useState(
    () => typeof window !== 'undefined' && window.sessionStorage.getItem('x1493_geo_banner_dismissed') === '1'
  );
  // @module:store-package-state — 套餐 state／selector 由 package-combo 注入於此行之下
  const [packages, setPackages] = useState<PackageConfigPublic[]>([]);
  // 套餐選配 modal：{ pkg, picks: Record<groupName, Set<productId>> }
  const [picking, setPicking] = useState<{ pkg: PackageConfigPublic; picks: Record<string, Set<number>> } | null>(null);
  const addPackage = useCartStore((s) => s.addPackage);

  useEffect(() => {
    trackPageView('/store');
    // Products arrive already brandified («X-1493») from the fetchProducts
    // data layer — no per-page transform needed here.
    fetchProducts().then((data) => {
      setProducts(data);
      // @module:store-package-fetch — 套餐清單載入由 package-combo 注入於此行之下
      // 套餐（公開端點回 raw array；失敗靜默——套餐區塊不顯示即可）
      apiFetchJson<PackageConfigPublic[]>('/public/package-configs')
        .then((res) => {
          const list = Array.isArray(res) ? res : ((res as unknown as { data?: PackageConfigPublic[] })?.data || []);
          if (Array.isArray(list) && list.length) setPackages(list);
        })
        .catch(() => {});
      setLoading(false);
    });
  }, []);

  const brandOptions = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => {
      if (brandGroupFilter === 'all' || p.brandGroup === brandGroupFilter) {
        if (p.brand) set.add(p.brand);
      }
    });
    return Array.from(set).sort();
  }, [products, brandGroupFilter]);

  const categoryOptions = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach((p) => {
      if (p.category) map.set(p.category, CATEGORY_LABELS[p.category] || p.category);
    });
    return Array.from(map.entries()).map(([slug, name]) => ({ slug, name }));
  }, [products]);

  const noFiltersActive =
    brandGroupFilter === 'all' && brandFilter === 'all' && categoryFilter === 'all' && searchText.trim() === '';

  const sortedProducts = useMemo(() => {
    let arr: Product[];
    if (noFiltersActive && sortBy === 'default') {
      arr = weightedShuffle(products, (p) => getProductWeight(p.createdAt), shuffleSeed);
    } else {
      arr = [...products];
      if (sortBy === 'price-asc') arr.sort((a, b) => a.price - b.price);
      else if (sortBy === 'price-desc') arr.sort((a, b) => b.price - a.price);
      else if (sortBy === 'new') arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      else arr.sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id));
    }
    // 海外 IP 訪客定向推送（輕量）：可出口商品排序提前——不隱藏、不限制瀏覽
    if (visitorRegion === 'overseas') {
      arr = [
        ...arr.filter((p) => !isLocalOnlyCategory(p.category)),
        ...arr.filter((p) => isLocalOnlyCategory(p.category)),
      ];
    }
    return arr;
  }, [products, noFiltersActive, sortBy, shuffleSeed, visitorRegion]);

  const searchLower = searchText.trim().toLowerCase();
  const filteredProducts = sortedProducts.filter((p) => {
    if (STORE_EXCLUDED_BRAND_GROUPS.includes(p.brandGroup || '')) return false;
    // 收貨地址為海外：隱藏「僅本地可售」商品（地址為王；IP 不參與購買限制）
    if (deliveryRegion === 'OS' && isLocalOnlyCategory(p.category)) return false;
    const groupMatch = brandGroupFilter === 'all' || p.brandGroup === brandGroupFilter;
    const brandMatch = brandFilter === 'all' || p.brand === brandFilter;
    const categoryMatch = categoryFilter === 'all' || p.category === categoryFilter;
    const searchMatch = !searchLower
      || p.name.toLowerCase().includes(searchLower)
      || (p.description || '').toLowerCase().includes(searchLower)
      || (p.brand || '').toLowerCase().includes(searchLower);
    return groupMatch && brandMatch && categoryMatch && searchMatch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [brandGroupFilter, brandFilter, categoryFilter, searchText, sortBy, shuffleSeed]);

  // @module:store-package-handlers — 套餐加入／選配 handlers 由 package-combo 注入於此行之下
  // 套餐加入：有自選組→開 modal；純必含→直接入車
  const handleAddPackage = (pkg: PackageConfigPublic) => {
    trackEvent('package_add_to_cart', { packageKey: pkg.key, hasChoiceGroups: pkg.choiceGroups.length > 0 });
    if (pkg.choiceGroups.length === 0) {
      addPackage(pkg, [], '');
      setCartOpen(true);
      return;
    }
    const picks: Record<string, Set<number>> = {};
    pkg.choiceGroups.forEach((g) => { picks[g.groupName] = new Set(); });
    setPicking({ pkg, picks });
  };

  const togglePick = (groupName: string, productId: number, chooseCount: number) => {
    setPicking((prev) => {
      if (!prev) return prev;
      const set = new Set(prev.picks[groupName]);
      if (set.has(productId)) set.delete(productId);
      else {
        if (set.size >= chooseCount) return prev; // 最多選 N
        set.add(productId);
      }
      return { ...prev, picks: { ...prev.picks, [groupName]: set } };
    });
  };

  const allGroupsSatisfied = picking
    ? picking.pkg.choiceGroups.every((g) => picking.picks[g.groupName]?.size === g.chooseCount)
    : false;

  const confirmPicking = () => {
    if (!picking || !allGroupsSatisfied) return;
    const selections: { productId: number; productName: string | null; groupName: string }[] = [];
    const parts: string[] = [];
    picking.pkg.choiceGroups.forEach((g) => {
      const chosen = g.options.filter((o) => picking.picks[g.groupName]?.has(o.productId));
      chosen.forEach((o) => selections.push({ productId: o.productId, productName: o.productName, groupName: g.groupName }));
      parts.push(`${g.groupName}：${chosen.map((o) => o.productName).join('＋')}`);
    });
    addPackage(picking.pkg, selections, parts.join('｜'));
    setPicking(null);
    setCartOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartSidebar isOpen={cartOpen} onClose={() => setCartOpen(false)} />
      {
        // @module:store-package-modal — 套餐選配 modal 由 package-combo 注入於此行之下
        picking ? (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-stone-100">
                <div>
                  <h3 className="font-bold text-lg">{picking.pkg.name}</h3>
                  <p className="text-sm text-stone-500">{formatPrice(picking.pkg.priceCents)} · 完成所有選擇後加入</p>
                </div>
                <button onClick={() => setPicking(null)} className="text-stone-400 hover:text-stone-600"><X size={22} /></button>
              </div>
              <div className="p-5 space-y-5">
                {picking.pkg.choiceGroups.map((g) => (
                  <div key={g.groupName}>
                    <p className="font-bold text-sm mb-2">
                      {g.groupName} <span className="text-x1493-primary">（任選 {g.chooseCount}）</span>
                      <span className="ml-2 text-stone-400 font-normal">已選 {picking.picks[g.groupName]?.size || 0}/{g.chooseCount}</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {g.options.map((o) => {
                        const picked = picking.picks[g.groupName]?.has(o.productId);
                        return (
                          <button
                            key={o.productId}
                            onClick={() => togglePick(g.groupName, o.productId, g.chooseCount)}
                            className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition ${
                              picked
                                ? 'border-x1493-primary bg-red-50 text-x1493-primary'
                                : 'border-stone-200 text-stone-600 hover:border-stone-300'
                            }`}
                          >
                            {o.productName}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <button
                  onClick={confirmPicking}
                  disabled={!allGroupsSatisfied}
                  className="w-full bg-x1493-primary text-white py-3 rounded-xl font-bold hover:bg-red-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {allGroupsSatisfied ? '加入購物車' : '請完成所有選擇'}
                </button>
              </div>
            </div>
          </div>
        ) : null
        // （運算式容器形式：未注入時容器為空、語法仍合法）
      }

      <section className="relative bg-x1493-dark text-white py-12 md:py-16 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(120% 140% at 15% 0%, #1a6b50 0%, #12503c 38%, #0b3527 100%)' }}
        />
        <div className="absolute inset-0 bg-x1493-dark/40" />
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{SITE_NAME} 商店</h1>
          <p className="text-white/80">香港品牌 · 直播試貨 · 包稅直郵——多品牌貨架任你揀</p>
        </div>
      </section>

      {
        // 海外 IP 訪客定向推送（輕量）：可出口商品優先——不隱藏、不限制瀏覽（session 內可關閉）
        visitorRegion === 'overseas' && !geoBannerDismissed ? (
          <div className="bg-amber-50 border-b border-amber-200">
            <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3 text-sm">
              <span className="text-lg">🌏</span>
              <p className="flex-1 text-amber-900">
                偵測到你位於海外——可出口商品已排前；冷凍及部分商品無法出境，最終可購範圍以收貨地址為準。
              </p>
              <button
                onClick={() => { setCategoryFilter('overseas'); setBrandGroupFilter('all'); setBrandFilter('all'); }}
                className="text-amber-900 font-bold underline underline-offset-2 hover:text-amber-700 whitespace-nowrap"
              >
                睇海外直郵貨架
              </button>
              <button
                onClick={() => {
                  setGeoBannerDismissed(true);
                  window.sessionStorage.setItem('x1493_geo_banner_dismissed', '1');
                }}
                aria-label="關閉提示"
                className="text-amber-500 hover:text-amber-700 p-1"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ) : null
      }

      <section className="py-8 sticky top-[72px] z-30 bg-white/95 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row gap-4 justify-between items-center">
          <div className="flex items-center gap-2 flex-wrap">
            <RegionSwitcher />
            <Filter size={18} className="text-stone-500" />
            <select
              value={brandGroupFilter}
              onChange={(e) => {
                setBrandGroupFilter(e.target.value);
                setBrandFilter('all');
              }}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全部品牌群組</option>
              {BRAND_GROUP_OPTIONS.map((b) => (
                <option key={b.slug} value={b.slug}>{b.name}</option>
              ))}
            </select>
            <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全部商戶</option>
              {brandOptions.map((b) => (
                <option key={b} value={b}>{brandLabel(b)}</option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全部類別</option>
              {categoryOptions.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            {noFiltersActive && sortBy === 'default' && (
              <button
                onClick={() => setShuffleSeed(Date.now())}
                className="flex items-center gap-1.5 text-sm font-bold text-x1493-primary border border-x1493-primary px-3 py-2 rounded-xl hover:bg-x1493-primary hover:text-white transition"
                title="隨機洗牌，新產品權重更高"
              >
                <span>隨機睇貨</span>
                <span>🎲</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
              <input
                type="search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="搜尋貨品…"
                className="pl-9 pr-3 py-2 border border-stone-200 rounded-xl text-sm w-40 sm:w-48 focus:ring-2 focus:ring-x1493-primary outline-none"
                aria-label="搜尋貨品"
              />
            </div>
            <div className="relative">
              <ArrowUpDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="pl-8 pr-3 py-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
                aria-label="排序方式"
              >
                <option value="default">預設排序</option>
                <option value="new">新到貨品</option>
                <option value="price-asc">價錢低至高</option>
                <option value="price-desc">價錢高至低</option>
              </select>
            </div>
          </div>

          <button
            onClick={() => setCartOpen(true)}
            className="flex items-center gap-2 bg-x1493-primary text-white px-5 py-2.5 rounded-xl font-bold hover:bg-red-700 transition"
          >
            <ShoppingCart size={20} />
            <span>購物車</span>
            {totalItems > 0 && (
              <span className="bg-white text-x1493-primary text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
            <span className="hidden sm:inline">· {formatPrice(subtotal)}</span>
          </button>
        </div>
      </section>

      {
        // @module:store-package-section — 套餐推介區塊由 package-combo 注入於此行之下
      packages.length > 0 ? (
        <section className="py-8 border-b border-stone-100">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-1 flex items-center gap-2"><Package className="text-x1493-primary" size={24} /> 套餐推介</h2>
            <p className="text-sm text-stone-500 mb-6">組合優惠價，一次買齊</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {packages.map((pkg) => (
                <div key={pkg.id} className="border-2 border-stone-200 rounded-2xl overflow-hidden flex hover:border-x1493-primary/50 transition bg-white">
                  <div className="w-32 sm:w-40 bg-stone-50 flex-shrink-0 aspect-square">
                    {pkg.imageUrl ? (
                      <img src={pkg.imageUrl} alt={pkg.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300"><Package size={40} /></div>
                    )}
                  </div>
                  <div className="flex-1 p-4 flex flex-col">
                    <h3 className="font-bold text-lg">{pkg.name}</h3>
                    {pkg.description && <p className="text-sm text-stone-500 line-clamp-2 mt-1">{pkg.description}</p>}
                    <div className="text-xs text-stone-500 mt-2 space-y-1 flex-1">
                      {pkg.fixedItems.length > 0 && (
                        <p>📦 必含：{pkg.fixedItems.map((f) => `${f.productName || ''}${f.quantity > 1 ? `×${f.quantity}` : ''}`).join('、')}</p>
                      )}
                      {pkg.choiceGroups.map((g) => (
                        <p key={g.groupName}>🎯 {g.groupName}：任選 {g.chooseCount}（{g.options.map((o) => o.productName).join(' / ')}）</p>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xl font-bold text-x1493-primary">{formatPrice(pkg.priceCents)}</p>
                      <button
                        onClick={() => handleAddPackage(pkg)}
                        className="bg-x1493-primary text-white px-4 py-2 rounded-xl font-bold hover:bg-red-700 transition flex items-center gap-1.5 text-sm"
                      >
                        <Plus size={16} /> 加入套餐
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null
        // （運算式容器形式：未注入時容器為空、語法仍合法）
      }

      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {loading ? (
                <div className="text-center py-20">載入中...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {paginatedProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm disabled:opacity-40 hover:bg-stone-50"
                  >
                    上一頁
                  </button>
                  <span className="text-sm text-stone-600">
                    第 {currentPage} / {totalPages} 頁（共 {filteredProducts.length} 件）
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm disabled:opacity-40 hover:bg-stone-50"
                  >
                    下一頁
                  </button>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-stone-50 rounded-2xl p-6 sticky top-40">
                <h3 className="font-bold text-lg mb-4">購物車摘要</h3>
                {totalItems === 0 ? (
                  <p className="text-stone-500 text-sm mb-4">購物車尚無商品</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">件數</span>
                      <span className="font-semibold">{totalItems}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold">
                      <span>小計</span>
                      <span className="text-x1493-primary">{formatPrice(subtotal)}</span>
                    </div>
                  </div>
                )}

                <PromoCodeInput orderAmount={subtotal} />

                <Link
                  to="/checkout"
                  className={`block w-full text-center mt-6 py-3 rounded-xl font-bold transition ${
                    totalItems > 0
                      ? 'bg-x1493-primary text-white hover:bg-red-700'
                      : 'bg-stone-200 text-stone-400 cursor-not-allowed pointer-events-none'
                  }`}
                >
                  前往結帳
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
